import type { Module } from "vuex/types/index.d.ts";
import { $api } from "@/services/api";
import store from "@/store";
import type { SchoolConfig, EnrollmentTerm, LoanConfig } from "@/models/application";
import type { School } from "@/models/school";
import type {
  LoanConfirmationDetails,
  RatePlanVM,
  RatePlanRepaymentVM,
  InitialDrawRequest,
  LoanOptionsConfiguration,
  LoanConfigurationModule,
} from "@/models/loanConfiguration";

const loanConfigurationModule: Module<LoanConfigurationModule, any> = {
  namespaced: true,
  state: {
    currentSubStep: 1,
    redirectToPortal: undefined as undefined | boolean,
    isEditing: false as boolean,
    savingChanges: false as boolean,
    initialDrawRequest: undefined as undefined | InitialDrawRequest,
    selectedRatePlan: undefined as undefined | RatePlanVM,
    selectedRateTerm: 0 as number,
    selectedRateType: "" as string,
    selectedRateTypeId: 0 as number,
    selectedRepaymentOption: {} as RatePlanRepaymentVM,
    selectedEnrollmentTerm: {} as EnrollmentTerm,
    selectedGraduationDate: "" as string,
    selectedSchool: {} as School,
    selectedRepaymentTypeId: 0 as number,
    enrollmentTerms: [] as EnrollmentTerm[],
    schoolConfiguration: {} as SchoolConfig,
    loanConfiguration: {} as LoanConfig,
    availableRatePlans: [] as RatePlanVM[],
    loanConfirmationDetails: undefined as undefined | LoanConfirmationDetails,
    loanOptionsConfiguration: {} as LoanOptionsConfiguration,
    fixedRatePlans: [] as RatePlanVM[],
    variableRatePlans: [] as RatePlanVM[],
    error: false as boolean,
    errorMessages: [] as string[],
    loading: false as boolean,
  },
  actions: {
    isCurrentSubStep({ state }, payload: number) {
      return state.currentSubStep === payload;
    },
    setSavingChanges({ commit }, flag: boolean) {
      commit("SET_SAVING_CHANGES", flag);
    },
    setCurrentSubStep({ commit }, payload: number) {
      commit("SET_CURRENT_SUBSTEP", payload);
    },
    setInitialDrawRequest({ commit }, payload: InitialDrawRequest) {
      commit("SET_INITIAL_DRAW_REQUEST", payload);
    },
    setSelectedRatePlan({ commit }, payload: RatePlanVM) {
      commit("SET_SELECTED_RATE_PLAN", payload);
    },
    setSchoolConfiguration({ commit }, payload: SchoolConfig) {
      commit("SET_SCHOOL_CONFIGURATION", payload);
    },
    setSelectedSchool({ commit }, school: School) {
      commit("SET_SELECTED_SCHOOL", school);
    },
    setSelectedEnrollmentTerm({ commit }, enrollmentTerm: EnrollmentTerm) {
      commit("SET_SELECTED_ENROLLMENT_TERM", enrollmentTerm);
    },
    setSelectedGraduationDate({ commit }, graduationDate: string) {
      commit("SET_SELECTED_GRADUATION_DATE", graduationDate);
    },
    setAvailableRatePlans({ commit }, ratePlans: RatePlanVM[]) {
      commit("SET_AVAILABLE_RATE_PLANS", ratePlans);
    },
    setLoanConfiguration({ commit }, loanConfiguration: LoanConfig) {
      commit("SET_LOAN_CONFIGURATION", loanConfiguration);
    },
    setSelectedRepaymentOption({ commit }, repaymentOption: RatePlanRepaymentVM) {
      commit("SET_SELECTED_REPAYMENT_OPTION", repaymentOption);
    },
    setLoanConfirmationDetails({ commit }, loanDetails: any) {
      commit("SET_LOAN_CONFIRMATION_DETAILS", loanDetails);
    },
    setSelectedRateTypeId({ commit }, rateTypeId: number) {
      commit("SET_SELECTED_RATE_TYPE_ID", rateTypeId);
    },
    async getInitialDraw({ commit }, applicationId: number) {
      const response = await $api.applications.getInitialDraw(applicationId);

      if (response.applicationId) {
        commit("SET_INITIAL_DRAW_REQUEST", response);
      }
    },
    async addOrUpdateInitialDraw({ state }, amount: number | string) {
      await store.dispatch("getEnrollmentTerms");

      if (state.schoolConfiguration?.schoolId) {
        const schoolId = state.schoolConfiguration?.schoolId;
        const enrollmentTerm = store.state.enrollmentTerms?.find(
          (p: EnrollmentTerm) =>
            p.description === store?.state?.appData?.application?.schoolConfiguration?.academicTerm,
        );

        const school = await $api.school.getSchool(schoolId);
        const index = store?.state?.appData?.applicants?.length - 1;

        const initialFundsRequest = {
          userId: store?.state?.appData?.applicants[index]?.userId, // last user added
          schoolDOEId: school.schoolDOEId,
          schoolBranchDOEId: school.schoolBranchDOEId,
          schoolDetailsId: school.schoolDetailsId,
          enrollmentTermId: enrollmentTerm?.id,
          amount: amount,
        } as InitialDrawRequest;

        const response = await $api.applications.addOrUpdateInitialDraw(
          store?.state?.appData.application.id,
          initialFundsRequest,
        );

        if (response.applicationId) {
          store.dispatch("setInitialDrawRequest", response);
        }

        return response;
      } else {
        return {
          errors: ["No School Selected"],
        };
      }
    },
    setFixedRatePlans({ commit }, ratePlans: RatePlanVM[]) {
      commit("SET_FIXED_RATE_PLANS", ratePlans);
    },
    setVariableRatePlans({ commit }, ratePlans: RatePlanVM[]) {
      commit("SET_VARIABLE_RATE_PLANS", ratePlans);
    },
    async getPelLoanOptions({ commit }) {
      commit("SET_LOADING", true);

      const applicationRatePlansResponse = await $api.applications.getPelLoanOptions(
        store.state.appData.application?.id,
      );

      if (applicationRatePlansResponse.success) {
        commit("SET_LOAN_OPTIONS_CONFIGURATION", applicationRatePlansResponse.results);

        commit("SET_SELECTED_REPAYMENT_OPTION", applicationRatePlansResponse.results.selectedRepaymentOption);

        commit("SET_SELECTED_RATE_PLAN", applicationRatePlansResponse.results.selectedRatePlan);

        if (applicationRatePlansResponse.results?.ratePlans) {
          store.dispatch("setAppRatePlans", applicationRatePlansResponse.results.ratePlans);
        }

        if (applicationRatePlansResponse.results.fixedRatePlans?.length > 0) {
          commit(
            "SET_FIXED_RATE_PLANS",
            applicationRatePlansResponse.results.fixedRatePlans.sort(
              (a: RatePlanVM, b: RatePlanVM) => a.repaymentTerm - b.repaymentTerm,
            ) || [],
          );
        }

        if (applicationRatePlansResponse.results.variableRatePlans?.length > 0) {
          commit(
            "SET_VARIABLE_RATE_PLANS",
            applicationRatePlansResponse.results.variableRatePlans.sort(
              (a: RatePlanVM, b: RatePlanVM) => a.repaymentTerm - b.repaymentTerm,
            ) || [],
          );
        }
      } else {
        const errors = [
          "Something has gone wrong retrieving rates. If you continue to receive this error, please contact support.",
        ];
        commit("SET_ERROR", true);
        commit("SET_ERROR_MESSAGES", errors);
      }

      commit("SET_LOADING", false);
    },
    async updateLoanConfiguration({ state, commit, dispatch }) {
      commit("SET_ERROR", false);
      commit("SET_ERROR_MESSAGES", []);

      const request = {
        applicationId: store.state?.appData?.application?.id,
        selectedRatePlanId: state.selectedRatePlan?.id,
        selectedByApplicantId: store.state.appData?.applicants[store.state.appData?.applicants.length - 1]?.id,
        repaymentTypeId: state.selectedRepaymentOption?.repaymentTypeId,
      };

      const response = await $api.applications.addOrUpdateAppLoanConfig(request);

      if (response && response.applicationId) {
        dispatch("setSavingChanges", false);
      } else {
        const errors = [
          "Something has gone wrong submitting your request. If you continue to receive this error, please contact support.",
        ];

        commit("SET_ERROR", true);
        commit("SET_ERROR_MESSAGES", errors);
      }
    },
  },
  mutations: {
    SET_CURRENT_SUBSTEP(state: any, substep: number) {
      state.currentSubStep = substep;
    },
    SET_SAVING_CHANGES(state: any, flag: boolean) {
      state.savingChanges = flag;
    },
    SET_INITIAL_DRAW_REQUEST(state: any, initialDrawRequest: any) {
      state.initialDrawRequest = initialDrawRequest;
    },
    SET_SELECTED_RATE_PLAN(state: any, selectedRatePlan: any) {
      state.selectedRatePlan = selectedRatePlan;
    },
    SET_SELECTED_RATE_TYPE_ID(state: LoanConfigurationModule, rateTypeId: number) {
      state.selectedRateTypeId = rateTypeId;
    },
    SET_SCHOOL_CONFIGURATION(state: any, schoolConfiguration: SchoolConfig) {
      state.schoolConfiguration = schoolConfiguration;
    },
    SET_SELECTED_SCHOOL(state: any, school: School) {
      state.selectedSchool = school;
    },
    SET_SELECTED_ENROLLMENT_TERM(state: any, enrollmentTerm: EnrollmentTerm) {
      state.selectedEnrollmentTerm = enrollmentTerm;
    },
    SET_SELECTED_GRADUATION_DATE(state: any, graduationDate: string) {
      state.selectedGraduationDate = graduationDate;
    },
    SET_LOAN_CONFIGURATION(state: any, loanConfiguration: LoanConfig) {
      state.loanConfiguration = loanConfiguration;
    },
    SET_AVAILABLE_RATE_PLANS(state: any, ratePlans: any) {
      state.availableRatePlans = ratePlans;
    },
    SET_SELECTED_REPAYMENT_OPTION(state: any, selectedRepaymentOption: any) {
      state.selectedRepaymentOption = selectedRepaymentOption;
    },
    SET_LOAN_CONFIRMATION_DETAILS(state: any, loanDetails: any) {
      state.loanConfirmationDetails = loanDetails;
    },
    SET_LOAN_OPTIONS_CONFIGURATION(state: any, loanOptionsConfiguration: any) {
      state.loanOptionsConfiguration = loanOptionsConfiguration;
    },
    SET_FIXED_RATE_PLANS(state: any, ratePlans: RatePlanVM[]) {
      state.fixedRatePlans = ratePlans;
    },
    SET_VARIABLE_RATE_PLANS(state: any, ratePlans: RatePlanVM[]) {
      state.variableRatePlans = ratePlans;
    },
    SET_ERROR(state: any, error: boolean) {
      state.error = error;
    },
    SET_ERROR_MESSAGES(state: any, errorMessages: string[]) {
      state.errorMessages = errorMessages;
    },
    SET_LOADING(state: LoanConfigurationModule, flag: boolean) {
      state.loading = flag;
    },
  },
  getters: {
    stepScroll: (state: any): string => {
      return `${(state.currentSubStep - 1) * 100}%`;
    },
    filteredRatePlans: (state: LoanConfigurationModule): RatePlanVM[] => {
      if (state.selectedRateTypeId === 1) {
        return state.fixedRatePlans;
      } else if (state.selectedRateTypeId === 2) {
        return state.variableRatePlans;
      } else {
        return [];
      }
    },
  },
};

export default loanConfigurationModule;
